import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  updateDoc,
  setDoc,
  Timestamp,
  getDoc,
  collection,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { UserData } from '../types/user';
import { userService } from '../services/userService';
import { ActiveSubscription } from '../services/stripeTypes';

export interface OrganisationMember {
  userId: string;
  role?: string;
  joinedAt?: Date;
  [key: string]: any;
}

export interface Organisation {
  id: string;
  name: string;
  createdAt?: Date;
  imageUrl?: string;
  description?: string;
  members: OrganisationMember[];
  [key: string]: any; // Allow for additional fields
}

export interface CoachWithSubscription {
  id: string;
  userData: UserData;
  subscription: ActiveSubscription | null;
}

export interface UseCoachesManagementReturn {
  coaches: CoachWithSubscription[];
  organisations: Organisation[];
  loading: boolean;
  error: Error | null;
  refreshData: () => Promise<void>;
  updateSubscription: (userId: string, subscriptionType: string) => Promise<void>;
}

export function useCoachesManagement(): UseCoachesManagementReturn {
  const [coaches, setCoaches] = useState<CoachWithSubscription[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrganisations = useCallback(async () => {
    console.log('[COACHES_MANAGEMENT] Fetching all organisations...');

    try {
      const orgsCollection = collection(db, 'organisations');
      const orgsSnapshot = await getDocs(orgsCollection);

      const BATCH_SIZE = 500;
      const orgIds = orgsSnapshot.docs.map(doc => doc.id);
      const organisationsList: Organisation[] = [];

      for (let i = 0; i < orgIds.length; i += BATCH_SIZE) {
        const batch = orgIds.slice(i, i + BATCH_SIZE);

        const orgPromises = batch.map(async (orgId) => {
          try {
            const orgDocRef = doc(db, 'organisations', orgId);
            const orgDoc = await getDoc(orgDocRef);

            if (!orgDoc.exists()) {
              return null;
            }

            const orgData = orgDoc.data();

            // Fetch members for this organization
            const membersCollection = collection(db, 'organisations', orgId, 'members');
            const membersSnapshot = await getDocs(membersCollection);

            const members: OrganisationMember[] = membersSnapshot.docs.map(memberDoc => {
              const memberData = memberDoc.data();
              return {
                userId: memberDoc.id,
                role: memberData.role,
                joinedAt: memberData.joinedAt?.toDate?.() || undefined,
                ...memberData
              };
            });

            return {
              id: orgId,
              name: orgData.name || 'Unknown Organization',
              createdAt: orgData.createdAt?.toDate?.() || undefined,
              members,
              ...orgData
            } as Organisation;

          } catch (error) {
            console.warn(`[COACHES_MANAGEMENT] Error fetching organization ${orgId}:`, error);
            return null;
          }
        });

        const results = await Promise.all(orgPromises);
        organisationsList.push(...results.filter((org): org is Organisation => org !== null));
      }

      console.log(`[COACHES_MANAGEMENT] Successfully fetched ${organisationsList.length} organisations with member data`);
      return organisationsList;

    } catch (error) {
      console.error('[COACHES_MANAGEMENT] Error fetching organisations:', error);
      return [];
    }
  }, []);

  const fetchCoaches = useCallback(async () => {
    console.log('[COACHES_MANAGEMENT] Fetching fresh coaches data...');
    setLoading(true);
    setError(null);

    try {
      // Fetch coaches and organisations in parallel
      const [coachesData, organisationsData] = await Promise.all([
        userService.getAllCoaches(),
        fetchOrganisations()
      ]);

      const coachIds = coachesData.map(({ id }) => id);

      // Batch fetch subscriptions for coaches only
      // const subscriptionsMap = new Map<string, UserSubscription>();
      const subscriptionsMap = new Map<string, ActiveSubscription>();

      // Process in batches to avoid too many concurrent requests
      const BATCH_SIZE = 10;

      // Fetch subscriptions
      for (let i = 0; i < coachIds.length; i += BATCH_SIZE) {
        const batch = coachIds.slice(i, i + BATCH_SIZE);

        const subscriptionPromises = batch.map(async (coachId) => {
          try {
            const subscriptionDoc = await userService.getUserSubscription(coachId);
            if (subscriptionDoc) {
              // Convert Firestore timestamps to dates
            //   let createdDate: Date;

            // createdDate = subscriptionDoc.created
            //   ? ('toDate' in subscriptionDoc.created ? subscriptionDoc.created.toDate() : new Date())
            //   : new Date();

            let lastUpdatedDate: Date;
            const lastUpdated = subscriptionDoc.lastUpdated;

            if (lastUpdated && typeof lastUpdated === 'object' && 'toDate' in lastUpdated && typeof lastUpdated.toDate === 'function') {
              lastUpdatedDate = lastUpdated.toDate();
            } else if (lastUpdated instanceof Date) {
              lastUpdatedDate = lastUpdated;
            } else {
              lastUpdatedDate = new Date();
            }

           subscriptionsMap.set(coachId, {
            userId: coachId,
            subscriptionType: subscriptionDoc.subscriptionType || 'unknown',
            subscriptionVia: subscriptionDoc.subscriptionVia || 'default',
            lastUpdated: lastUpdatedDate,
            subscriptionChangeMessage: subscriptionDoc.subscriptionChangeMessage || '',
            subscriptionChangeRead: subscriptionDoc.subscriptionChangeRead ?? true,
          });
            }
          } catch (error) {
            console.warn(`[COACHES_MANAGEMENT] Error fetching subscription for coach ${coachId}:`, error);
          }
        });

        await Promise.all(subscriptionPromises);
      }

      // Combine coaches with their subscriptions and sort by name
      const coachesWithSubscriptions: CoachWithSubscription[] = coachesData
        .map(({ id, userData }) => ({
          id,
          userData,
          subscription: subscriptionsMap.get(id) || null,
        }))
        .sort((a, b) => (a.userData.name || '').localeCompare(b.userData.name || ''));

      console.log(`[COACHES_MANAGEMENT] Successfully fetched ${coachesWithSubscriptions.length} coaches`);

      setCoaches(coachesWithSubscriptions);
      setOrganisations(organisationsData);
      setError(null);

    } catch (err) {
      console.error('[COACHES_MANAGEMENT] Error fetching coaches:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch coaches'));
    } finally {
      setLoading(false);
    }
  }, [fetchOrganisations]);

  const refreshData = useCallback(async () => {
    await fetchCoaches();
  }, [fetchCoaches]);

  const updateSubscription = useCallback(async (userId: string, subscriptionType: string) => {
    console.log(`[COACHES_MANAGEMENT] Updating subscription for coach ${userId} to ${subscriptionType}`);

    try {
      // THIS IS LEGACY CODE, NEED TO UPDATE TO USE ACTIVE SUBSCRIPTIONS COLLECTION
      const subscriptionRef = doc(db, 'userSubscriptions', userId);

      // Check if subscription document exists by checking our current coaches data
      const existingCoach = coaches.find(c => c.id === userId);

      if (existingCoach?.subscription) {
        // Update existing subscription
        await updateDoc(subscriptionRef, {
          subscriptionType,
          lastUpdated: Timestamp.now()
        });
      } else {
        // Create new subscription document
        await setDoc(subscriptionRef, {
          userId,
          subscriptionType,
          created: Timestamp.now(),
          lastUpdated: Timestamp.now()
        });
      }

      console.log(`[COACHES_MANAGEMENT] Successfully updated subscription for coach ${userId}`);

      // Refresh data to show the changes
      await fetchCoaches();

    } catch (err) {
      console.error(`[COACHES_MANAGEMENT] Error updating subscription for coach ${userId}:`, err);
      throw err;
    }
  }, [coaches, fetchCoaches]);

  useEffect(() => {
    fetchCoaches();
  }, [fetchCoaches]);

  return {
    coaches,
    organisations,
    loading,
    error,
    refreshData,
    updateSubscription,
  };
}
